import type { FC, ReactNode } from 'react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import AddMessageForm from './components/AddMessageForm';
import Comment from './components/Comment';
import AddMoreBtn from '@components/AddMoreBtn';
import SortList from '@components/SortList';
import { ProductReviewCard, ReviewCard } from '@ui';
import { ELEMENTS_COMMENTS, ELEMENTS_REVIEWS } from '@constants/ElementsLimit';
import { declension } from '@utils/declension';
import { observer } from 'mobx-react';
import type { ProductPageMethods } from '@routes/ProductPage/index.type';
import type {
  CommentsItemsFragmentFragment,
  CommentsTypeTypeEnum,
  GetArticlesCommentsByArticleIdQuery
} from '@schema/cart';
import { ReviewabilityStatus } from '@schema/cart';
import type { RecipeCommentsResolverT } from '@routes/Recipe/component';
import type { NewsSingleCommentsResolverT } from '@routes/NewsSingle/component';
import usePreviousValue from '@customHooks/usePreviousValue';
import { useStores } from '@root/src/stores';
import './CommentsBlock.scss';

interface CommentsBlockProps {
  isBarMenu?: boolean;
  showWithFileBtn?: boolean;
  productId?: string | number;
  commentType: CommentsTypeTypeEnum;
  resolver:
    | NewsSingleCommentsResolverT
    | ProductPageMethods['_commentsResolver']
    | RecipeCommentsResolverT
    | (() => Promise<
        GetArticlesCommentsByArticleIdQuery['getArticlesCommentsByArticleId']
      >);
  title;
  objectId: number;
  changeTabToComments?: () => void;
  isSortList?: boolean;
  sortList?: any;
  sortListValue?: any;
  sortListCallback?: any;
  sortlistInRow?: any;
  objectType?: 'PRODUCT';
  newTypeComment?: boolean;
  isQuestion?: boolean;
  productPage?: boolean;
  isShowReviewRules?: boolean;
  modalSuccessText?: string;
  modalSuccessTitle?: string;
  addCommentInstance;
  isProductAvailableForRefund?: boolean;
  withReturnForm?: boolean;
  formPlaceholder?: string;
  bestComment?: ReactNode | null;
  isVesovoi?: boolean;
  commentsItemsName:
    | 'commentsItems'
    | 'articlesComments'
    | 'recipeCommentsItem';
}

const CommentsBlock: FC<CommentsBlockProps> = observer(
  ({
    showWithFileBtn,
    productId,
    title,
    objectId,
    isSortList,
    sortList,
    sortListValue,
    sortListCallback,
    sortlistInRow,
    objectType,
    newTypeComment,
    isQuestion,
    isShowReviewRules,
    modalSuccessText,
    modalSuccessTitle,
    addCommentInstance,
    formPlaceholder,
    resolver,
    changeTabToComments,
    commentsItemsName,
    commentType,
    bestComment = null,
    withReturnForm = false,
    isProductAvailableForRefund = false,
    isVesovoi = false,
    isBarMenu = false
  }) => {
    const { userStore } = useStores();
    const { isUserLoading, isUserLoggedIn } = userStore;

    const [list, setList] = useState<CommentsItemsFragmentFragment[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [withFile, setWithFile] = useState(false);
    const [reviewabilityStatus, setReviewabilityStatus] = useState(
      ReviewabilityStatus.NotBought
    );

    const _loadData = useCallback(
      async (page: number): Promise<void> => {
        if (typeof resolver !== 'function' || !objectId) {
          return;
        }

        await resolver(
          page,
          commentType === 'Review' ? ELEMENTS_REVIEWS : ELEMENTS_COMMENTS,
          commentType,
          withFile
        )
          .then(res => {
            if (res) {
              let updatedRes;
              if (res && commentsItemsName) {
                updatedRes = res[commentsItemsName];
              } else {
                updatedRes = res;
              }

              const updatedComments =
                page > 1 ? list.slice().concat(updatedRes) : updatedRes;
              const activePage = page > 1 ? currentPage + 1 : 1;

              setList(updatedComments || []);
              setTotalCount(res.totalCount || 0);
              setCurrentPage(activePage);
              setReviewabilityStatus(res.reviewabilityStatus);
            }
          })
          .catch(() => {});
      },
      [
        commentType,
        commentsItemsName,
        currentPage,
        list,
        objectId,
        resolver,
        withFile
      ]
    );

    useEffect(() => {
      _loadData(1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const prevWithFile = usePreviousValue(withFile);
    const prevSortListValue = usePreviousValue(sortListValue);
    const prevObjectId = usePreviousValue(objectId);

    useEffect(() => {
      if (
        prevWithFile !== withFile ||
        prevSortListValue !== sortListValue ||
        prevObjectId !== objectId
      ) {
        _loadData(1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      objectId,
      prevObjectId,
      prevSortListValue,
      prevWithFile,
      sortListValue,
      withFile
    ]);

    const loadMoreComments = (): void => {
      _loadData(currentPage + 1);
    };

    const onSuccessfullMessageSend = (): void => {
      _loadData(currentPage);
    };

    const onWithFileToggler = (): void => {
      setWithFile(prev => !prev);
    };

    const balanceItems =
      totalCount -
      currentPage *
        (commentType === 'Review' ? ELEMENTS_REVIEWS : ELEMENTS_COMMENTS);
    const isCurrentTabQuestion = location.href.includes('questions');

    const titleCommentsBlock = useMemo((): ReturnType<typeof declension> => {
      if (commentType === 'Review') {
        return declension(totalCount, ['отзыв', 'отзыва', 'отзывов']);
      } else if (commentType === 'Comment') {
        return declension(totalCount, [
          'комментарий',
          'комментария',
          'комментариев'
        ]);
      } else {
        return declension(totalCount, ['вопрос', 'вопроса', 'вопросов']);
      }
    }, [commentType, totalCount]);

    const headingCommentsBlock =
      commentType === 'Review'
        ? 'Отзывы'
        : commentType === 'Comment'
        ? 'Комментарии'
        : 'Вопросы';
    const setLimit =
      commentType === 'Review' ? ELEMENTS_REVIEWS : ELEMENTS_COMMENTS;

    return (
      <div className='main-container-mess'>
        <AddMessageForm
          reviewabilityStatus={reviewabilityStatus}
          formPlaceholder={formPlaceholder}
          addCommentInstance={addCommentInstance}
          productId={productId}
          title={title}
          isShowReviewRules={isShowReviewRules}
          objectId={objectId}
          commentType={commentType}
          isQuestion={isQuestion}
          modalSuccessText={modalSuccessText}
          modalSuccessTitle={modalSuccessTitle}
          changeTabToComments={changeTabToComments}
          isProductAvailableForRefund={isProductAvailableForRefund}
          withReturnForm={withReturnForm}
          onSuccessfullMessageSend={onSuccessfullMessageSend}
          isVesovoi={isVesovoi}
        />
        {!isUserLoading && !isUserLoggedIn && totalCount > 0 && (
          <div
            className={`main-mess-header mess-header-add ${
              isSortList ? 'mess-header-sortlist' : ''
            }`}
          >
            <div className='additional-heading'>{headingCommentsBlock}</div>
          </div>
        )}

        {totalCount > 0 && (
          <Fragment>
            <div className='total-count-comments'>
              <div className='total-count-comments__count'>
                Всего <span>{totalCount || 0}</span> {titleCommentsBlock}{' '}
              </div>
              {isSortList && (
                <SortList
                  sortlist={sortList}
                  value={sortListValue}
                  callback={sortListCallback}
                  sortlistInRow={sortlistInRow}
                  isQuestionTab={isCurrentTabQuestion}
                  withFile={withFile}
                  onWithFileToggler={onWithFileToggler}
                  showWithFileBtn={showWithFileBtn}
                />
              )}
            </div>
            {/* Лучший отзыв */}
            {bestComment && bestComment}
            <div className='mess-container'>
              {list.map(message => {
                if (newTypeComment) {
                  if (objectType === 'PRODUCT') {
                    return (
                      <ProductReviewCard
                        key={message.id}
                        message={message}
                        isBarMenu={isBarMenu}
                        onEditOrDeleteReviewSubmit={() => {
                          _loadData(currentPage);
                        }}
                        type={'productPage'}
                      />
                    );
                  }
                  return (
                    <ReviewCard
                      key={message?.id}
                      message={message}
                      onSuccessfulRateAnswer={() => {
                        _loadData(currentPage);
                      }}
                      onEditOrDeleteReviewCallback={() => {
                        _loadData(currentPage);
                      }}
                      reviewType='ProductReview'
                    />
                  );
                } else {
                  return <Comment key={message.id} comment={message} />;
                }
              })}
            </div>

            {totalCount - list.length > 0 && balanceItems > 0 && (
              <AddMoreBtn
                callback={loadMoreComments}
                balanceItems={balanceItems > setLimit ? setLimit : balanceItems}
                titleArray={['комментарий', 'комментария', 'комментариев']}
              />
            )}
          </Fragment>
        )}
      </div>
    );
  }
);

export default CommentsBlock;
